import { Modal, SearchInput } from "@msys/ui";
import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { CategoryFragment } from "../../categories/useCategories.generated.js";
import { CatalogueGrid } from "../offeredServices/CatalogueGrid.js";
import {
  expandedNonLeafIds,
  type CatalogueBuffer,
  type TaxonomyIndex,
} from "../offeredServices/catalogueModel.js";
import {
  AUTO_FILLED_PROJECT_TYPES,
  OFFERED_PROJECT_TYPES,
} from "../offeredServices/projectTypes.js";

interface Props {
  categories: CategoryFragment[];
  index: TaxonomyIndex;
  /** What the draft starts from: the trades and chips, or the matrix applied before. */
  initialBuffer: CatalogueBuffer;
  /** Apply: the draft becomes the services' matrix. */
  onApply: (buffer: CatalogueBuffer) => void;
  /** Cancel or close: the draft is discarded. */
  onCancel: () => void;
}

/**
 * "Specify your offered services": the onboarding catalogue grid (category tree
 * with cascade activation, five project-type columns, search) in a modal. The
 * user edits a draft; only Apply hands it over.
 */
export const OnboardingMatrixModal = ({
  categories,
  index,
  initialBuffer,
  onApply,
  onCancel,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const [draft, setDraft] = React.useState(initialBuffer);
  const [search, setSearch] = React.useState("");

  // Every non-leaf category with something declared below it opens on entry, so
  // the declared services are visible. Derived from the starting buffer, so it
  // is stable and does not fight the user's own expand/collapse.
  const initiallyExpandedIds = React.useMemo(
    () => expandedNonLeafIds(initialBuffer.declared, index),
    [initialBuffer, index]
  );

  return (
    <Modal
      title={t("Specify your offered services", { ns: "OrganisationProfile" })}
      maxWidth="lg"
      // The stack only marks a modal as top after its first render; hidden
      // until then, the grid would be measured at zero width.
      alwaysVisible
      handleClose={onCancel}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: onCancel,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Apply", { ns: "OrganisationProfile" }),
          handleClick: () => onApply(draft),
        },
      ]}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1,
            backgroundColor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <SearchInput
            // SearchInput appends the "…" to the placeholder itself.
            placeholder={t("Search categories", { ns: "OrganisationProfile" })}
            inputProps={{
              "aria-label": t("Search categories", {
                ns: "OrganisationProfile",
              }),
            }}
            value={search}
            onChangeSearchTerm={setSearch}
            size="extra-small"
            sx={{
              transition: "ease-in-out 250ms max-width",
              maxWidth: "100%",
              ":not(:focus-within)": { maxWidth: 320 },
            }}
          />
        </Box>
        <CatalogueGrid
          categories={categories}
          index={index}
          buffer={draft}
          search={search}
          projectTypes={OFFERED_PROJECT_TYPES}
          autoFillProjectTypes={AUTO_FILLED_PROJECT_TYPES}
          onBufferChange={setDraft}
          showOverrides={false}
          initiallyExpandedIds={initiallyExpandedIds}
        />
      </Stack>
    </Modal>
  );
};
